import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { logEvent, CUSTOMER_EVENTS } from "../../../utils/amplitude";
import { useAccess } from "../../../hooks/useAccess";
import AppLoader from "../../../components/loader";
import {
  useCustomerById,
  useFetchCustomerProjects
} from "../../../apolloClient/hooks/general/fetchHook";
import { TxtInput } from "../../../components";
import moment from "moment";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import searchIcon from "../../../images/searchIcon.png";

import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox,
  SearchIcon,
  TableWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  PrimaryCell,
  SecondaryText,
  StatusBadge,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";

import styled from "@emotion/styled";

// Detail Page Specific Styles
const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background-color: #e5e7eb;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #d1d5db;
  }
`;

const InfoCard = styled(Box)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin-bottom: 24px;
`;

const InfoGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media only screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const InfoItem = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #1a1a2e;
  font-weight: 500;
`;

const SectionHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;

  @media only screen and (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background-color: #4b87ed;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;

  &:hover {
    background-color: #3a6fd8;
  }

  @media only screen and (min-width: 768px) {
    width: auto;
  }
`;

export const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loading: accessLoading } = useAccess();
  const inputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [keyword, setKeyword] = useState("");
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressData, setAddressData] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: ""
  });

  const customerId = parseInt(id || '0');

  const { customer, loading: customerLoading, error: customerError } = useCustomerById({
    id: customerId,
  });

  const {
    customerProjects,
    loading: projectsLoading,
    error: projectsError
  } = useFetchCustomerProjects({
    customerId,
  });

  // Track customer viewed
  useEffect(() => {
    if (customer && !customerLoading) {
      logEvent(CUSTOMER_EVENTS.CUSTOMER_VIEWED, {
        customer_id: customer.id,
        customer_email: customer.email,
        project_count: customerProjects?.length || 0
      });
    }
  }, [customer, customerLoading, customerProjects]);

  const handleBackToCustomers = () => {
    navigate('/customers');
  };

  const handleViewProject = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCreateProject = () => {
    // Check if customer has address
    if (customer.address && customer.address.length > 0) {
      // Customer has address, proceed to customer project creation page
      navigate(`/customer-project-create/${customerId}`);
    } else {
      // Customer doesn't have address, need to collect address first
      setShowAddressModal(true);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    const totalPages = Math.ceil(filterProjects().length / rowsPerPage);
    setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = moment(dateString);
    if (!date.isValid()) return 'N/A';
    return date.format('MM/DD/YYYY');
  };

  const filterProjects = () => {
    if (!customerProjects) return [];
    if (keyword.length > 0) {
      return customerProjects.filter((project: any) => {
        return (
          project.projectRefNumber?.toLowerCase().includes(keyword.toLowerCase()) ||
          project.id?.toString().includes(keyword) ||
          project.status?.toLowerCase().includes(keyword.toLowerCase())
        );
      });
    }
    return customerProjects;
  };

  const handleAddressChange = (field: string, value: string) => {
    setAddressData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressSubmit = async () => {
    try {
      // TODO: Implement address creation API call here
      // const addressInput = {
      //   street: addressData.street,
      //   city: addressData.city,
      //   state: addressData.state,
      //   zipCode: addressData.zipCode,
      //   customerId: customerId
      // };

      // Close modal and proceed to customer project creation page
      setShowAddressModal(false);
      navigate(`/customer-project-create/${customerId}`);
    } catch (error) {
      console.error('Error creating address:', error);
      // Handle error - show toast or error message
    }
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setAddressData({
      street: "",
      city: "",
      state: "",
      zipCode: ""
    });
  };

  if (accessLoading || customerLoading || projectsLoading) {
    return <AppLoader isLoading={accessLoading || customerLoading || projectsLoading} />;
  }

  if (customerError || projectsError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <EmptyState>
            <EmptyStateText>
              Error loading customer data: {customerError?.message || projectsError?.message}
            </EmptyStateText>
          </EmptyState>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!customer) {
    return (
      <PageContainer>
        <ContentWrapper>
          <EmptyState>
            <EmptyStateText>Customer not found</EmptyStateText>
          </EmptyState>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const fullName = customer.firstName && customer.lastName
    ? `${customer.firstName} ${customer.lastName}`
    : customer.firstName || customer.lastName || "N/A";

  const filteredProjects = filterProjects();
  const paginatedProjects = filteredProjects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(filteredProjects.length / rowsPerPage);

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Header */}
        <HeaderSection>
          <BackButton onClick={handleBackToCustomers}>
            ← Back to Customers
          </BackButton>
          <Box sx={{ mt: 2 }}>
            <TitleRow>
              <PageTitle>{fullName}</PageTitle>
            </TitleRow>
            <PageSubtitle>Customer #{customer.id}</PageSubtitle>
          </Box>
        </HeaderSection>

        {/* Customer Info Card */}
        <InfoCard>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{customer.email || "N/A"}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>{customer.phone || "N/A"}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Member Since</InfoLabel>
              <InfoValue>{formatDate(customer.createdAt)}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </InfoCard>

        {/* Projects Section */}
        <SectionHeader>
          <Box>
            <SectionTitle>Projects</SectionTitle>
            <SecondaryText>{filteredProjects.length} total projects</SecondaryText>
          </Box>
          <ActionButton onClick={handleCreateProject}>
            + Create New Project
          </ActionButton>
        </SectionHeader>

        <Box sx={{ mb: 3 }}>
          <SearchInputBox>
            <SearchIcon
              src={searchIcon}
              style={{ margin: "0 12px" }}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search projects..."
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInputBox>
        </Box>

        <TableWrapper>
          <TableHeader>
            <TableHeaderCell width="35%">Project Ref</TableHeaderCell>
            <TableHeaderCell width="25%">Project ID</TableHeaderCell>
            <TableHeaderCell width="25%">Created</TableHeaderCell>
            <TableHeaderCell width="15%"></TableHeaderCell>
          </TableHeader>

          {filteredProjects.length === 0 ? (
            <EmptyState>
              <EmptyStateText>
                {keyword ? "No projects found matching your search" : "No projects yet"}
              </EmptyStateText>
              {!keyword && (
                <Box sx={{ mt: 2 }}>
                  <ActionButton onClick={handleCreateProject}>
                    Create First Project
                  </ActionButton>
                </Box>
              )}
            </EmptyState>
          ) : (
            paginatedProjects.map((project: any) => (
              <TableRow key={project.id} onClick={() => handleViewProject(project.id)}>
                <PrimaryCell width="35%" mobileLabel="Project Ref">
                  <StatusBadge variant="info">#{project.projectRefNumber}</StatusBadge>
                </PrimaryCell>
                <TableCell width="25%" mobileLabel="Project ID">
                  {project.id}
                </TableCell>
                <TableCell width="25%" mobileLabel="Created">
                  <SecondaryText>{formatDate(project.createdAt)}</SecondaryText>
                </TableCell>
                <TableCell width="15%" style={{ textAlign: "right" }}>
                  <Box
                    component="span"
                    sx={{
                      color: "#4b87ed",
                      fontSize: 13,
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View →
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}

          {filteredProjects.length > 0 && (
            <PaginationWrapper>
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <PaginationText>
                Page {page + 1} of {totalPages}
              </PaginationText>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </PaginationWrapper>
          )}
        </TableWrapper>

        {/* Address Collection Modal */}
        <Dialog
          open={showAddressModal}
          onClose={handleCloseAddressModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "12px" }
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: 18, color: "#1a1a2e" }}>
            Add Customer Address
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <SecondaryText>
                Please enter the customer's address to proceed with project creation.
              </SecondaryText>

              <Box>
                <InfoLabel style={{ display: "block", marginBottom: 6 }}>Street Address</InfoLabel>
                <TextField
                  fullWidth
                  placeholder="Enter street address"
                  value={addressData.street}
                  onChange={(e) => handleAddressChange("street", e.target.value)}
                  size="small"
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                <Box sx={{ flex: 1 }}>
                  <InfoLabel style={{ display: "block", marginBottom: 6 }}>City</InfoLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter city"
                    value={addressData.city}
                    onChange={(e) => handleAddressChange("city", e.target.value)}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <InfoLabel style={{ display: "block", marginBottom: 6 }}>State</InfoLabel>
                  <TextField
                    fullWidth
                    placeholder="Enter state"
                    value={addressData.state}
                    onChange={(e) => handleAddressChange("state", e.target.value)}
                    size="small"
                  />
                </Box>
              </Box>

              <Box>
                <InfoLabel style={{ display: "block", marginBottom: 6 }}>Zip Code</InfoLabel>
                <TextField
                  fullWidth
                  placeholder="Enter zip code"
                  value={addressData.zipCode}
                  onChange={(e) => handleAddressChange("zipCode", e.target.value)}
                  size="small"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleCloseAddressModal}
              variant="outlined"
              sx={{
                color: "#6b7280",
                borderColor: "#e5e7eb",
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": { borderColor: "#d1d5db", backgroundColor: "#f9fafb" }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddressSubmit}
              variant="contained"
              disabled={!addressData.street || !addressData.city || !addressData.state}
              sx={{
                backgroundColor: "#4b87ed",
                borderRadius: "8px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#3a6fd8" }
              }}
            >
              Create Project
            </Button>
          </DialogActions>
        </Dialog>
      </ContentWrapper>
    </PageContainer>
  );
};